<template>
  <div class="form-outline">
    <form-legend :field="field" class="pb-3" />
    <v-input :ref="'range-' + field.cid" :rules="rules" :hint="field.field_options.description" persistent-hint v-bind="darkOrLightMode">
      <v-col class="pa-0">
        <v-row>
          <v-col class="col-12 col-md-6">
            <component
              :is="getMappedComponent(field.field_options.range_type)"
              :field="getFieldFrom()"
              :formModel="formModel"
              :mediasModel="mediasModel"
              :readonly="readonly"
              :mode="mode"
              @formUpdated="formUpdated"
            />
          </v-col>
          <v-col class="col-12 col-md-6">
            <component
              :is="getMappedComponent(field.field_options.range_type)"
              :field="getFieldTo()"
              :formModel="formModel"
              :mediasModel="mediasModel"
              :readonly="readonly"
              :mode="mode"
              @formUpdated="formUpdated"
            />
          </v-col>
        </v-row>
        <legend>{{ getFieldValue() }} {{ getType() }}</legend>
      </v-col>
      <template v-slot:message="{ message }">
        <legend v-html="message"></legend>
      </template>
    </v-input>
  </div>
</template>

<script>
import DateEditor from './DateEditor.vue';
import TimeEditor from './TimeEditor.vue';
import TextFieldEditor from './TextFieldEditor.vue';
import formsMixin from '../formsMixin';
import FormLegend from '@/modules/forms/FormLegend.vue';

export default {
  components: { FormLegend },
  mixins: [formsMixin],
  emits: ['formUpdated'],
  props: {
    field: { type: Object, required: true },
    formModel: { type: Object, required: true },
    mediasModel: { type: Array, required: true },
    readonly: { type: Boolean, default: false },
    mode: { type: String, default: 'light' },
  },
  data() {
    return {
      fieldValue: this.formModel[this.field.cid] ? this.formModel[this.field.cid] : '',
      fromValue: this.formModel[this.field.cid + '_from'] ? this.formModel[this.field.cid + '_from'] : '',
      toValue: this.formModel[this.field.cid + '_to'] ? this.formModel[this.field.cid + '_to'] : '',
      rules: [this.validateMinRange, this.validateFromIsBefore],
    };
  },
  computed: {
    fromField() {
      return this.fromValue;
    },
    toField() {
      return this.toValue;
    },
  },
  methods: {
    formUpdated(k, v) {
      this.$emit('formUpdated', k, v);
    },
    getMappedComponent(type) {
      switch (type) {
        case 'number':
          return TextFieldEditor;
        case 'date':
          return DateEditor;
        case 'time':
          return TimeEditor;
        default:
          return null;
      }
    },
    getFieldFrom() {
      return {
        required: this.field.required,
        label: this.field.field_options.range_from,
        cid: this.field.cid + '_from',
        field_options: {
          min: this.field.field_options.range_min,
          max: this.field.field_options.range_max,
          minError: this.field.field_options.range_min_error,
          maxError: this.field.field_options.range_max_error,
        },
        field_type: this.field.field_options.range_type,
      };
    },
    getFieldTo() {
      return {
        required: this.field.required,
        label: this.field.field_options.range_to,
        cid: this.field.cid + '_to',
        field_options: {
          min: this.field.field_options.range_min,
          max: this.field.field_options.range_max,
          minError: this.field.field_options.range_min_error,
          maxError: this.field.field_options.range_max_error,
        },
        field_type: this.field.field_options.range_type,
      };
    },
    getType() {
      if (this.fieldValue && this.field.field_options.range_type !== 'number' && !(this.field.field_options.range_type === 'time')) {
        //Don't show type for number and time if format is hh:mm (because is pre-mapped)
        return this.$t(this.field.field_options.range_dateformat);
      }
    },
    getFieldValue() {
      const diffInMinutes = this.fieldValue;
      if (this.field.field_options.range_type === 'time' && this.field.field_options.range_timeformat === 'hh:mm' && diffInMinutes) {
        let hours = Math.floor(Math.abs(diffInMinutes) / 60);
        let minutes = Math.abs(diffInMinutes) % 60;
        return `${diffInMinutes > 0 ? '' : '-'}${hours}h ${minutes}min`;
      } else if (this.field.field_options.range_type === 'time' && diffInMinutes) {
        return diffInMinutes + 'min';
      } else {
        return diffInMinutes;
      }
    },
    onFieldUpdate(from, to) {
      if (from !== undefined && to !== undefined) {
        if (this.field.field_options.range_type === 'number') {
          this.fieldValue = to - from;
        } else if (this.field.field_options.range_type === 'date') {
          //Set date difference
          this.fieldValue = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
        } else if (this.field.field_options.range_type === 'time') {
          //Calculate time difference in minutes
          const fromHours = Number(from.split(':')[0]);
          const fromMinutes = Number(from.split(':')[1]);
          const toHours = Number(to.split(':')[0]);
          const toMinutes = Number(to.split(':')[1]);

          const fromInMinutes = fromHours * 60 + fromMinutes;
          const toInMinutes = toHours * 60 + toMinutes;
          console.debug(`fromInMinutes=${fromInMinutes} toInMinutes=${toInMinutes}`);
          let offset = toInMinutes < fromInMinutes ? 24 * 60 : 0;
          this.fieldValue = toInMinutes + offset - fromInMinutes;
        }
      }
    },
    validateMinRange() {
      const min = this.field.field_options.range_min;
      if (min === undefined || min <= this.fieldValue) {
        return true;
      }
      return this.$t('field.minRange', { min });
    },
    validateFromIsBefore() {
      if (this.fieldValue < 0) {
        return this.$t('field.min', { min: 0 });
      }
      return true;
    },
  },
  watch: {
    formModel(newValue) {
      const value = newValue[this.field.cid] ? newValue[this.field.cid] : '';
      const from = newValue[this.field.cid + '_from'] ? newValue[this.field.cid + '_from'] : '';
      const to = newValue[this.field.cid + '_to'] ? newValue[this.field.cid + '_to'] : '';
      if (this.fieldValue != value) {
        this.fieldValue = value;
      }
      if (this.fromValue != from) {
        this.fromValue = from;
      }
      if (this.toValue != to) {
        this.toValue = to;
      }
    },
    fieldValue(newValue) {
      if (newValue) {
        this.$refs['range-' + this.field.cid].validate();
      }
      this.$emit('formUpdated', this.field.cid, newValue);
    },
    fromField(newVal) {
      this.onFieldUpdate(newVal, this.toField);
    },
    toField(newVal) {
      this.onFieldUpdate(this.fromField, newVal);
    },
  },
};
</script>
